import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

import { BdbSessionStrgService, BdbEncryptService } from '@npm-bbta/bbog-cm-auth-lib';
import { DispatcherService } from './dispatcher.service';
import { GoogleTagManagerService } from './google-tag-manager.service';
import { MediumTypeEnum } from '../enums/medium-type.enum';
import { ChannelService } from './channel.service';
import { Router } from '@angular/router';
import { MantizService } from './mantiz.service';
import { ICypherResponseModel } from '../models/cypher-response.model';
import { CypherService } from './cypher.service';
import { IMantizResponseModel } from '../models/mantizResponse.model';
import { SessionStorageKeysEnum } from '../models/session-storage-keys-enum';
import { EventsService } from './bdb-event.service';
import HttpStatusCode from '../enums/http-status.enum';
import { HardwareType } from '../enums/hardware-type.enum';
import { ProductsEnum } from '../enums/products-enum';
import { NewServiceErrorEnum } from '../enums/new-service-error.enum';
import { ErrorCode } from '../models/error-codes-v2';
import { AssistedStorage } from './assistedStorage/assistedStorage';
import { InitialLoad } from './assistedStorage/models/IAssitedStorage';

const AUTHENTICATOR_URL = 'authenticator-handler';
const REDIRECT_AUTHENTICATION_EVENT = 'redirectFromOffice'

@Injectable()
export class RedirectService {
    constructor(
        private readonly bdbEncryptService: BdbEncryptService,
        private readonly dispatcher: DispatcherService,
        private readonly googleTagManagerService: GoogleTagManagerService,
        private readonly sessionStorageService: BdbSessionStrgService,
        private readonly channelService: ChannelService,
        private readonly router: Router,
        private readonly mantizService: MantizService,
        private readonly eventsService: EventsService,
        private readonly assistedStorage: AssistedStorage
    ) { }

    redirectFromAuthentication() {
        const site = this.sessionStorageService.get(SessionStorageKeysEnum.SITE);
        if (site) {
            const hardware = this.sessionStorageService.get(SessionStorageKeysEnum.HARDWARE);
            const statusCode = hardware === HardwareType.OLD ? HttpStatusCode.OK : HttpStatusCode.CREATED;
            const resultOperation = HttpStatusCode[statusCode];
            this.eventsService.reportEvent(REDIRECT_AUTHENTICATION_EVENT, ' ', resultOperation, null, null, null)
            this.shareData();
        } else {
            this.eventsService.reportEvent(REDIRECT_AUTHENTICATION_EVENT, ' ', '500', null, null, null)
            throw new Error('Site parameter missing');
        }
    }

    // Enable only for digital account
    siteIsAllowedToRedirectOnAuthFailure() {
        return this.sessionStorageService.get(SessionStorageKeysEnum.SITE) === '4';
    }

    redirectFailedAuthentication() {
        this.sessionStorageService.save('failedAuth', 'true');
        this.redirectFromAuthentication();
    }

    private shareData() {
        const recognitionFrontDataAsString = this.sessionStorageService.get('front');
        const recognitionFrontData = JSON.parse(recognitionFrontDataAsString);
        const recognitionBackDataAsString = this.sessionStorageService.get('back');
        const recognitionBackData = JSON.parse(recognitionBackDataAsString);
        const dataToShare = {
            accessToken: this.sessionStorageService.get(SessionStorageKeysEnum.ACCESS_TOKEN),
            authInfo: this.sessionStorageService.get(SessionStorageKeysEnum.AUTH_INFO),
            email: this.sessionStorageService.get(SessionStorageKeysEnum.EMAIL),
            cellPhone: this.sessionStorageService.get(SessionStorageKeysEnum.CELL_PHONE_NUMBER),
            identityNumber: this.sessionStorageService.get(SessionStorageKeysEnum.IDENTITY_NUMBER),
            identityType: this.sessionStorageService.get(SessionStorageKeysEnum.IDENTITY_TYPE),
            keyAuthenticatedCellPhone: this.sessionStorageService.get(SessionStorageKeysEnum.CELL_PHONE_NUMBER),
            accessType: this.getAccessType(),
            optionCode: this.sessionStorageService.get('optionCode'),
            productId: this.sessionStorageService.get('product'),
            officeCode: this.sessionStorageService.get(SessionStorageKeysEnum.OFFICE_CODE),
            fromLauncher: 'true',
            reactivationAccount: this.sessionStorageService.get('reactivationAccount'),
            appVersion: this.sessionStorageService.get('appVersionSite'),
            specificInfoForProduct: this.sessionStorageService.get('specificInfoForProduct'),
            uuidThreat: this.sessionStorageService.get('uuidThreat'),
            customerState: this.sessionStorageService.get(SessionStorageKeysEnum.CUSTOMER_STATE),
            network: this.sessionStorageService.get(SessionStorageKeysEnum.PRIVATE_IP),
            channel: this.channelService.getChannel(),
            utmUuid: this.sessionStorageService.get('utmUuid'),
            specificProductInfoUuid: this.sessionStorageService.get(SessionStorageKeysEnum.SPECIFIC_PRODUCT_INFO_UUID),
            authUuid: this.sessionStorageService.get('uuidAuth'),
            branch: this.sessionStorageService.get(SessionStorageKeysEnum.BRANCH),
            site: this.sessionStorageService.get(SessionStorageKeysEnum.SITE),
            recognition: {
                front: recognitionFrontData,
                back: recognitionBackData
            },
            seller: {
                sellerIdentityNumber: this.sessionStorageService.get(SessionStorageKeysEnum.SELLER_IDENTITY_NUMBER),
                sellerIdentityNumberLdap: this.sessionStorageService.get(SessionStorageKeysEnum.SELLER_IDENTITY_NUMBER_LDAP),
                sellerUsername: this.sessionStorageService.get(SessionStorageKeysEnum.SELLER_USER_NAME)
            }
        };
        this.redirectDispatcher(dataToShare);
    }

    async redirectDispatcher(dataToShare: any) {
        const initialLoad: InitialLoad = await this.assistedStorage.getByKey('initialLoad');
        this.getEncryptDataToDispatcher(dataToShare);
        if (initialLoad.useSandboxDispatcher) {
            this.dispatcher.saveInfoDispacherSandbox(dataToShare)
                .then(response => {
                    if (response.status === 201) {
                        const responseAuth = response.body;
                        this.redirectCase(responseAuth.uuid);
                    } else {
                        throw new Error('Error create sandbox uuid');
                    }
                });
        } else {
            this.dispatcher.saveInfoAuthenticated(dataToShare).subscribe(response => {
                const responseAuth = JSON.parse(response._body);
                if (response.status === 201) {
                    this.redirectCase(responseAuth.uuid);
                } else {
                   throw new Error('Error create uuid');
                }
            });
        }
    }

    async redirectCase(uuid: string) {
        await this.eventsService.reportEvent('endAuthentication', '200', 'OK', null, null, null);
                const site = +this.sessionStorageService.get(SessionStorageKeysEnum.SITE);
                const configUrl = environment.registeredApplications[site];
                let redirectToUrl = '';
                switch (site) {
                    case 12:
                        try {
                            redirectToUrl = await this.getUrlForMantiz();
                        } catch (e) {
                            console.log('Error get url for mantiz');
                            this.quit();
                        }
                        break;
                    case 15:
                    case 16:
                        redirectToUrl = `${configUrl.baseUrl}?uuid=${uuid}`;
                        break;
                    case 3:
                    case 5:
                    case 10:
                    case 17:
                    case 23:
                    case 28:
                    case 40:
                    case 47:
                    default:
                        redirectToUrl = `${configUrl.baseUrl}${AUTHENTICATOR_URL}?uuid=${uuid}`;
                        break;
                }
                console.log('redirectToUrl', redirectToUrl);
                this.googleTagManagerService.sendInfo('/auth-success', 'Autenticación exitosa Oficina');
                window.location.href = redirectToUrl;
    }

    async getUrlForMantiz() {
        try {
            const dataMantiz = await this.mantizService.getUrlMantiz();
            console.log('response Mantiz ', dataMantiz);
            console.log('type Mantiz ', typeof (dataMantiz));

            let responseMantiz;
            try {
                responseMantiz = JSON.parse(dataMantiz) as ICypherResponseModel;
            } catch (e) {
                responseMantiz = dataMantiz;
            }

            const key = responseMantiz.EndDt;
            const data = responseMantiz.Message;
            const mantizResponse = CypherService.decrypt(data, key) as IMantizResponseModel;
            console.log('response MantizDecrypt ', mantizResponse);
            return mantizResponse.url;
        } catch (e) {
            console.log('ERROR URL MANTIZ ', e);
            throw new Error('Error url for mantiz');
        }
    }

    getEncryptDataToDispatcher(dataToShare: any) {
        const identityNumberEncrypt = this.bdbEncryptService.encrypt(dataToShare.identityNumber);
        dataToShare.identityNumber = identityNumberEncrypt;
        try {
            const accessTokenEncrypt = this.bdbEncryptService.encrypt(dataToShare.accessToken);
            dataToShare.accessToken = accessTokenEncrypt;
        } catch (error) {
            console.error('Error -->', error);
        }
    }

    saveInitialParams() {
        const mvp: string = this.sessionStorageService.get(SessionStorageKeysEnum.SITE);
        const product: string = ProductsEnum[mvp].name;
        const officeCode: string = this.sessionStorageService.get(SessionStorageKeysEnum.OFFICE_CODE);
        const validateOfficeCode: string = this.sessionStorageService.get('validateOfficeCode');
        const optionCode: string = this.sessionStorageService.get('optionCode');
        const reactivationAccount: string = this.sessionStorageService.get('reactivationAccount');
        const specificInfoForProduct: string = this.sessionStorageService.get('specificInfoForProduct');
        const utmUuid: string = this.sessionStorageService.get('utmUuid');
        const specificProductInfoUuid: string = this.sessionStorageService.get(SessionStorageKeysEnum.SPECIFIC_PRODUCT_INFO_UUID);

        if (!mvp) {
            throw new Error('site to redirect missing');
        }

        this.sessionStorageService.save(SessionStorageKeysEnum.SITE, mvp);
        if (product) {
            this.sessionStorageService.save('product', product);
        }
        if (validateOfficeCode) {
            this.sessionStorageService.save('validateOfficeCode', validateOfficeCode);
        }
        if (optionCode) {
            this.sessionStorageService.save('optionCode', optionCode);
        }
        if (reactivationAccount) {
            this.sessionStorageService.save('reactivationAccount', reactivationAccount);
        }
        if (this.sessionStorageService.get(SessionStorageKeysEnum.OFFICE_CODE) === null) {
            this.sessionStorageService.save(SessionStorageKeysEnum.OFFICE_CODE, officeCode);
        }
        if (specificInfoForProduct) {
            this.sessionStorageService.save('specificInfoForProduct', specificInfoForProduct);
        }
        if (utmUuid) {
            this.sessionStorageService.save('utmUuid', utmUuid);
        }
        if (specificProductInfoUuid) {
            this.sessionStorageService.save(SessionStorageKeysEnum.SPECIFIC_PRODUCT_INFO_UUID, specificProductInfoUuid);
        }
        this.sessionStorageService.save('baseUrl', environment.registeredApplications[mvp].baseUrl);
    }

    checkInitialParams(): boolean {
        const officeCodeValidate = this.sessionStorageService.get(SessionStorageKeysEnum.OFFICE_CODE);
        const validEnviroment =
            (officeCodeValidate === 'null' || officeCodeValidate === 'undefined')
            || (officeCodeValidate !== 'null' && officeCodeValidate !== 'undefined');

        return (
            this.sessionStorageService.get(SessionStorageKeysEnum.SITE) !== null &&
            this.sessionStorageService.get('product') !== null &&
            validEnviroment &&
            this.sessionStorageService.get('baseUrl') != null &&
            this.sessionStorageService.get('validateOfficeCode') !== null &&
            this.sessionStorageService.get('reactivationAccount') !== null
        );
    }

    quit() {
        const mediumType = this.sessionStorageService.get(SessionStorageKeysEnum.MEDIUM_TYPE);
        const site = this.sessionStorageService.get(SessionStorageKeysEnum.SITE);
        const allowedMediumTypeRedirectToLanding = [MediumTypeEnum.MICROFINANCES.toString(), MediumTypeEnum.OFFICE.toString()];
        const isMediumTypeAllowedRedirectToLanding = allowedMediumTypeRedirectToLanding.includes(mediumType);
        const urlWithParams = this.sessionStorageService.get('queryParamsComplete');
        const urlLanding = `${environment.landingAuthenticatorOffice}?${urlWithParams}`;
        if ((mediumType && isMediumTypeAllowedRedirectToLanding) || site === '12') {
            console.log('redirecting to landing...');
            this.sessionStorageService.save('identityNumber', '');
            this.sessionStorageService.save('userAcceptedTAC', 'false');
            if (mediumType === MediumTypeEnum.MICROFINANCES.toString() || site === '12') {
                this.router.navigate(['']);
            } else {
                window.location.href = urlLanding;
            }
        } else {
            const redirectToUrl = environment.registeredApplications['2'].baseUrl;
            console.log('redirecting to launcher...');
            this.sessionStorageService.clear();
            window.location.href = redirectToUrl;
        }
    }

    redirectToError(errorCode: NewServiceErrorEnum): void
    redirectToError(errorCode: ErrorCode, version: 'v2'): void
    redirectToError(errorCode: ErrorCode, version: 'v2', clearData: boolean): void
    redirectToError(errorCode: string, version: 'v1' | 'v2' = 'v1', clearData = true): void {
        if (version === 'v1') {
            this.sessionStorageService.save(SessionStorageKeysEnum.ERROR_CODE, errorCode);
            this.router.navigate(['error']);
        } else {
            this.router.navigate(['error-v2'], { queryParams: { code: errorCode, clearData} });
        }
    }

    redirectToIdentityNumber(): void {
        this.sessionStorageService.save('identityNumber', '');
        this.sessionStorageService.save('userAcceptedTAC', 'false');
        this.router.navigate(['']);
    }

    navigate(...args: Parameters<typeof Router.prototype.navigate>): void {
        this.router.navigate(...args);
    }

    private getAccessType(): number {
        return 2;
    }

    public redirectToUrl(redirectToUrl: string): void {
        this.sessionStorageService.clear();
        window.location.href = redirectToUrl;
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/redirect.service.ts